import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, useMediaQuery, useTheme,
  Badge, Tooltip, Divider, Select, MenuItem, Collapse
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import TableChartIcon from '@mui/icons-material/TableChart';
import LuggageIcon from '@mui/icons-material/Luggage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ForumIcon from '@mui/icons-material/Forum';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import BusinessIcon from '@mui/icons-material/Business';
import PaymentIcon from '@mui/icons-material/Payment';
import AnchorIcon from '@mui/icons-material/Anchor';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import HistoryIcon from '@mui/icons-material/History';
import DnsIcon from '@mui/icons-material/Dns';
import GavelIcon from '@mui/icons-material/Gavel';
import BugReportIcon from '@mui/icons-material/BugReport';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorageIcon from '@mui/icons-material/Storage';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import HubIcon from '@mui/icons-material/Hub';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CategoryIcon from '@mui/icons-material/Category';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { WavePattern } from './common/NaraIllustrations';
import PoliticaAuditoriaGate from './common/PoliticaAuditoriaGate';

const HUB_URL = 'http://24.199.87.96:3005';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 60;

// allowedRoles: si presente, sólo se muestra a esos roles. Si ausente, se muestra
// a todos EXCEPTO comercial (comercial es allowlist puro — sólo ve lo marcado).
// adminOnly se mantiene por compatibilidad → equivale a allowedRoles=['admin','superadmin'].
const menuItems = [
  { text: 'Catálogo', icon: <LuggageIcon />, path: '/catalogo/paquetes', matchPath: '/catalogo', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro', 'operaciones'] },
  { text: 'Pasadías', icon: <CalendarMonthIcon />, path: '/pasadias/salidas', matchPath: '/pasadias', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro', 'operaciones'] },
  { text: 'Comercial', icon: <PersonSearchIcon />, path: '/comercial/ventas', matchPath: '/comercial', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin'] },
  { text: 'Booking', icon: <BookOnlineIcon />, path: '/booking', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro', 'operaciones'] },
  {
    text: 'Muelle',
    icon: <AnchorIcon />,
    matchPath: '/muelle',
    allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'operaciones'],
    children: [
      { text: 'Recepción', icon: <AnchorIcon />, path: '/muelle/recepcion', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'operaciones'] },
      { text: 'Cierre',    icon: <ReceiptLongIcon />, path: '/muelle/cierre', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'operaciones'] },
    ],
  },
  { text: 'Charter', icon: <DirectionsBoatIcon />, path: '/charter/listado', matchPath: '/charter', allowedRoles: ['operaciones', 'admin', 'superadmin'] },
  { text: 'Agencias', icon: <BusinessIcon />, path: '/agencias/lista', matchPath: '/agencias', allowedRoles: ['admin', 'superadmin', 'miembro', 'administrativo'] },
  // Comercial entra a Finanzas pero solo ve No-shows, CxC (cupo) y
  // Comisiones por pagar, y en modo consulta. La lista blanca vive en
  // FinanzasLayout; el backend además bloquea toda escritura suya bajo
  // los prefijos de finanzas.
  // Su primera pantalla es CxC, no cartera: cartera no está en su lista.
  { text: 'Finanzas', icon: <AccountBalanceWalletIcon />, path: '/finanzas/cxc', matchPath: '/finanzas', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro'], badgeKey: 'cruce' },
  // Gerencial — espacio para preguntas de dirección: proyecciones, planeación
  // de caja, KPIs consolidados. NO es sub-sección de Finanzas: la audiencia
  // es gerencia, no operación. Solo admin/superadmin lo ven en el menú.
  { text: 'Gerencial', icon: <TrendingUpIcon />, path: '/gerencial', matchPath: '/gerencial', allowedRoles: ['admin', 'superadmin'] },
  { text: 'Flota', icon: <BuildIcon />, path: '/flota/embarcaciones', matchPath: '/flota', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro', 'operaciones', 'piloto'] },
  { text: 'Trazabilidad', icon: <HistoryIcon />, path: '/trazabilidad', allowedRoles: ['admin', 'superadmin'] },
  { text: 'Analiticas', icon: <AnalyticsIcon />, path: '/analytics', allowedRoles: ['admin', 'superadmin'] },
  {
    text: 'Reportes',
    icon: <AssessmentIcon />,
    matchPath: '/reportes',
    allowedRoles: ['admin', 'administrativo', 'superadmin', 'miembro'],
    children: [
      { text: 'Embarques por rango', icon: <DirectionsBoatIcon />, path: '/reportes/embarques', allowedRoles: ['admin', 'administrativo', 'superadmin', 'miembro', 'operaciones'] },
      { text: 'Pasajeros por tour', icon: <BookOnlineIcon />, path: '/reportes/pasajeros-por-tour', allowedRoles: ['admin', 'administrativo', 'superadmin', 'miembro'] },
      { text: 'Anexo detallado de ventas', icon: <TableChartIcon />, path: '/reportes/anexo-ventas', allowedRoles: ['admin', 'administrativo', 'superadmin', 'miembro'] },
      { text: 'Charters', icon: <DirectionsBoatIcon />, path: '/reportes/charters', allowedRoles: ['operaciones', 'admin', 'superadmin'] },
    ],
  },
  { divider: true, label: 'Chatbots' },
  { text: 'Smart Flows', icon: <AutoAwesomeIcon />, path: '/smart-flows', allowedRoles: ['admin', 'superadmin'] },
  { text: 'Conversaciones', icon: <ForumIcon />, path: '/conversaciones', badgeKey: 'conversaciones', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro'] },
  { divider: true, label: 'Gestión' },
  { text: 'Mi Actividad', icon: <HistoryIcon />, path: '/mi-actividad', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro', 'operaciones', 'piloto', 'administrativo'] },
  { text: 'Mi Equipo', icon: <GroupsIcon />, path: '/mi-equipo', adminOnly: true },
  { text: 'Intermediarios', icon: <GroupsIcon />, path: '/intermediarios', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro'] },
  { text: 'Configuración', icon: <SettingsIcon />, path: '/configuracion', allowedRoles: ['admin', 'superadmin'] },
  { text: 'Pasarelas de Pago', icon: <PaymentIcon />, path: '/configuracion/pasarelas', adminOnly: true },
  { text: 'Cuentas Destino', icon: <AccountBalanceIcon />, path: '/admin/cuentas-destino', allowedRoles: ['admin', 'administrativo', 'superadmin'] },
  { text: 'Marco Legal · Datos', icon: <GavelIcon />, path: '/marco-legal', allowedRoles: ['comercial', 'admin', 'administrativo', 'superadmin', 'miembro'] },
  { text: 'Mi Suscripcion', icon: <CardMembershipIcon />, path: '/suscripcion' },
];

const adminItems = [
  { text: 'Usuarios Plataforma', icon: <PeopleIcon />, path: '/admin/usuarios' },
  { text: 'Tipos de Empresa', icon: <CategoryIcon />, path: '/admin/tipos-empresa' },
  { text: 'Zona Horaria', icon: <LanguageIcon />, path: '/admin/timezone' },
  { text: 'Consumo Servicios', icon: <DataUsageIcon />, path: '/consumo-tokens' },
  { text: 'Planes', icon: <AdminPanelSettingsIcon />, path: '/admin/planes' },
  { text: 'Servicios', icon: <DnsIcon />, path: '/admin/servicios' },
  { text: 'Politicas', icon: <GavelIcon />, path: '/admin/politicas' },
  { text: 'Tests', icon: <BugReportIcon />, path: '/admin/tests' },
  { text: 'Correcciones IA', icon: <PsychologyIcon />, path: '/admin/corrections' },
  { text: 'Base de Datos', icon: <StorageIcon />, path: '/admin/db' },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState('miembro');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [badges, setBadges] = useState({ conversaciones: 0, citas: 0, cruce: 0 });
  const [empresas, setEmpresas] = useState([]);
  const [empresaActiva, setEmpresaActiva] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; }
  });

  const drawerWidth = isMobile ? DRAWER_WIDTH : (collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUserName(u.displayName || 'Usuario');
        setUserEmail(u.email || '');
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api'}/users/by_uid/${u.uid}`);
          const data = await res.json();
          setIsSuperAdmin(data?.role === 'superadmin');
          setIsAdmin(data?.role === 'admin' || data?.role === 'superadmin');
          setUserRole(data?.role || 'miembro');
          // Guardar user_id para que otros componentes lo usen en X-User-Id header
          if (data?.id) {
            localStorage.setItem('user_id', data.id);
          }
          // Refrescar el perfil en localStorage (rol + empresa) desde el backend.
          //
          // La condición era `if (data?.company_id)`, y el endpoint by_uid no
          // devolvía ese campo: el refresco no corría nunca y el `user` de
          // localStorage quedaba congelado en el del último login. Como la
          // sesión de Firebase dura meses, un usuario al que se le cambiaba el
          // rol en BD seguía navegando con el rol viejo y los gates que leen
          // localStorage (esSoloConsulta de CxC, FinanzasLayout, etc.) le
          // ocultaban acciones que ya tenía permitidas.
          //
          // Ahora la condición es el rol: es el dato que gobierna los permisos
          // y siempre viene en la respuesta. El company_id se conserva si el
          // backend no lo manda, para no perder el que guardó el login.
          if (data?.role) {
            const savedEmpresa = localStorage.getItem('empresa_activa');
            let prev = {};
            try { prev = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
            const cid = savedEmpresa
              || (data.company_id != null ? String(data.company_id) : null)
              || prev.company_id
              || null;
            localStorage.setItem("user", JSON.stringify({
              company_id: cid, role: data.role,
              name: data.name, email: data.email
            }));
            if (cid) setEmpresaActiva(cid);
          }
          // Cargar lista de empresas si es superadmin
          if (data?.role === 'superadmin') {
            try {
              const empRes = await fetch(`${process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api'}/admin/empresas`);
              if (empRes.ok) {
                const empData = await empRes.json();
                setEmpresas(Array.isArray(empData) ? empData : empData.empresas || []);
              }
            } catch {}
          }
        } catch (e) {
          console.error("Error cargando perfil:", e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchBadges = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const storedUserId = localStorage.getItem('user_id');
      if (!storedUserId) return;
      const headers = { 'X-User-Id': String(storedUserId), 'Content-Type': 'application/json' };
      const empresaActiva = localStorage.getItem('empresa_activa');
      const empresaParam = empresaActiva ? `empresa_id=${empresaActiva}` : '';

      const [convRes, citasRes, cruceRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api'}/conversaciones/resumen-dia?${empresaParam}`, { headers }).catch(() => null),
        fetch(`${process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api'}/emr/citas/hoy?${empresaParam}`, { headers }).catch(() => null),
        empresaActiva ? fetch(`${process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api'}/turismo/cruce/badge?${empresaParam}`, { headers }).catch(() => null) : null,
      ]);

      const convData = convRes?.ok ? await convRes.json() : null;
      const citasData = citasRes?.ok ? await citasRes.json() : null;
      const cruceData = cruceRes?.ok ? await cruceRes.json() : null;

      setBadges({
        conversaciones: convData?.conversaciones_activas || 0,
        citas: citasData?.total || 0,
        cruce: cruceData?.count || 0,
      });
    } catch (e) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  const cambiarEmpresa = (newEmpresaId) => {
    setEmpresaActiva(newEmpresaId);
    localStorage.setItem('empresa_activa', newEmpresaId);
    // Actualizar el user en localStorage
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.company_id = newEmpresaId;
      localStorage.setItem('user', JSON.stringify(user));
    } catch {}
    // Recargar para que todos los componentes usen la nueva empresa
    window.location.reload();
  };

  const isCollapsed = !isMobile && collapsed;

  // Filtro por rol:
  //  - Roles "allowlist puro" (comercial, operaciones): SOLO ven items con
  //    allowedRoles que incluyan explícitamente su rol. Los items sin
  //    allowedRoles quedan ocultos por default.
  //  - admin/superadmin/miembro: se respeta adminOnly y allowedRoles (si está
  //    definido). Sin allowedRoles → visible por default.
  const ROLES_ALLOWLIST_PURO = ['comercial', 'operaciones'];
  const esAllowlistPuro = ROLES_ALLOWLIST_PURO.includes(userRole);
  const isVisible = (it) => {
    if (it.divider) {
      // Los roles restringidos no necesitan dividers (lista corta).
      return !esAllowlistPuro;
    }
    if (esAllowlistPuro) {
      return Array.isArray(it.allowedRoles) && it.allowedRoles.includes(userRole);
    }
    if (it.adminOnly && !isAdmin) return false;
    if (Array.isArray(it.allowedRoles) && !it.allowedRoles.includes(userRole)) return false;
    return true;
  };

  const filterByRole = (items) => items
    .filter(isVisible)
    .map(it => it.children
      ? { ...it, children: it.children.filter(isVisible) }
      : it)
    .filter(it => !it.children || it.children.length > 0);

  const allItems = [
    ...filterByRole(menuItems),
    ...(isSuperAdmin ? [{ divider: true, label: 'Admin' }, ...adminItems] : []),
  ];

  const renderIcon = (item) => {
    const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
    if (badgeCount > 0) {
      return (
        <Badge badgeContent={badgeCount} color="error" max={99}>
          {item.icon}
        </Badge>
      );
    }
    return item.icon;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        p: isCollapsed ? 1.5 : 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        gap: isCollapsed ? 0 : 1.5,
        background: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
        transition: 'padding 0.2s ease',
        position: 'relative',
      }}>
        <WavePattern />
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 38, height: 38, objectFit: 'contain' }} />
        </Box>
        {!isCollapsed && (
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.2, fontFamily: '"Outfit", sans-serif' }}>
              NARA Tourism
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
              Chatbots, Booking y Paquetes
            </Typography>
          </Box>
        )}
      </Box>

      {/* Selector de empresa (solo superadmin) */}
      {isSuperAdmin && empresas.length > 0 && !isCollapsed && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Select
            value={empresaActiva}
            onChange={(e) => cambiarEmpresa(e.target.value)}
            size="small"
            fullWidth
            sx={{
              fontSize: 12, fontWeight: 600, color: '#134e4a',
              bgcolor: '#f0fdfa', borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#0891b230' },
              '& .MuiSelect-select': { py: 0.75 },
            }}
          >
            {empresas.map(e => (
              <MenuItem key={e.id} value={String(e.id)} sx={{ fontSize: 12 }}>
                {e.company || e.nombre || `Empresa ${e.id}`}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <List sx={{ flex: 1, px: isCollapsed ? 0.5 : 1.5, pt: 1.5, overflowY: 'auto' }}>
        {allItems.map((item, idx) => {
          if (item.divider) {
            return isCollapsed ? (
              <Divider key={`div-${idx}`} sx={{ my: 1, mx: 0.5 }} />
            ) : (
              <Box key={`div-${idx}`} sx={{ mt: 1.5, mb: 0.5, px: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {item.label}
                </Typography>
              </Box>
            );
          }

          // Item con sub-opciones (acordeón)
          if (item.children?.length) {
            return (
              <NavGroup
                key={item.text}
                item={item}
                isCollapsed={isCollapsed}
                location={location}
                navigate={navigate}
                onNavigate={() => setMobileOpen(false)}
                renderIcon={renderIcon}
              />
            );
          }

          const matchBase = item.matchPath || item.path;
          const selected = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(matchBase));

          const button = (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2,
                  bgcolor: selected ? 'rgba(30, 58, 95, 0.08)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  px: isCollapsed ? 1 : 2,
                }}
              >
                <ListItemIcon sx={{
                  color: selected ? '#164e63' : '#94a3b8',
                  minWidth: isCollapsed ? 0 : 40,
                  justifyContent: 'center',
                }}>
                  {renderIcon(item)}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      noWrap: true,
                      title: item.text,   // tooltip nativo si se llegara a truncar
                    }}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: selected ? 600 : 400,
                        color: selected ? '#164e63' : '#64748b',
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );

          return isCollapsed ? (
            <Tooltip key={item.text} title={item.text} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={item.text}>{button}</React.Fragment>
          );
        })}
      </List>

      {/* Toggle button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5, borderTop: '1px solid #e2e8f0' }}>
          <IconButton onClick={toggleCollapsed} size="small" sx={{ color: '#94a3b8' }}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}

      {/* User info */}
      <Box sx={{ p: isCollapsed ? 0.5 : 1.5, borderTop: '1px solid #e2e8f0' }}>
        {!isCollapsed && (
          <Box sx={{ px: 1, pb: 1 }}>
            <Typography sx={{ color: '#1e293b', fontSize: 12, fontWeight: 600 }}>
              {userName}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 10 }}>
              {userEmail}
            </Typography>
            {/* Chip permanente: recordatorio de que la sesión es auditada.
                Origen: incidente 28-jul-2026, blindaje UX según decisión "C". */}
            <Tooltip title="Todas tus acciones en el sistema quedan registradas con tu email, IP y hora exacta. Consulta tu registro en Gestión → Mi Actividad." placement="right" arrow>
              <Box sx={{
                mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 0.75, py: 0.25, borderRadius: 1,
                bgcolor: 'rgba(30, 58, 95, 0.08)',
                border: '1px solid rgba(30, 58, 95, 0.15)',
                cursor: 'help',
              }}>
                <Typography sx={{ fontSize: 9, color: '#1e3a5f', fontWeight: 700, letterSpacing: 0.3 }}>
                  🔒 SESIÓN AUDITADA
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        )}
        {localStorage.getItem('hub_origin') === 'true' ? (
          <Tooltip title={isCollapsed ? 'Volver al Hub' : ''} placement="right" arrow>
            <ListItemButton
              onClick={() => {
                window.location.href = HUB_URL;
              }}
              sx={{
                borderRadius: 2,
                py: 0.75,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ color: '#164e63', minWidth: isCollapsed ? 0 : 36, justifyContent: 'center' }}>
                <HubIcon fontSize="small" />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText primary="Volver al Hub"
                  sx={{ '& .MuiListItemText-primary': { fontSize: 13, color: '#164e63', fontWeight: 500 } }} />
              )}
            </ListItemButton>
          </Tooltip>
        ) : (
          <Tooltip title={isCollapsed ? 'Cerrar sesion' : ''} placement="right" arrow>
            <ListItemButton
              onClick={async () => {
                await signOut(auth);
                localStorage.removeItem('user_id');
                navigate('/login');
              }}
              sx={{
                borderRadius: 2,
                py: 0.75,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ color: '#ef4444', minWidth: isCollapsed ? 0 : 36, justifyContent: 'center' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText primary="Cerrar sesion"
                  sx={{ '& .MuiListItemText-primary': { fontSize: 13, color: '#ef4444', fontWeight: 500 } }} />
              )}
            </ListItemButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
   <PoliticaAuditoriaGate>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0fdfa' }}>
      {isMobile && (
        <AppBar position="fixed" sx={{
          background: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
          boxShadow: 'none',
        }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%', mx: 1,
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </Box>
            <Typography sx={{ fontWeight: 700 }}>NARA Tourism</Typography>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s ease',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            bgcolor: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
            transition: 'width 0.2s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box sx={{
        flex: 1,
        mt: isMobile ? 8 : 0,
        maxWidth: '100%',
        overflow: 'auto',
        transition: 'margin-left 0.2s ease',
      }}>
        {process.env.REACT_APP_ENV === 'test' && (
          <Box sx={{
            bgcolor: '#f59e0b', color: '#fff', textAlign: 'center',
            py: 0.5, px: 2, fontSize: 12, fontWeight: 700, letterSpacing: 1,
          }}>
            ENTORNO DE PRUEBAS — Los datos aquí no afectan producción
          </Box>
        )}
        {children}
      </Box>
    </Box>
   </PoliticaAuditoriaGate>
  );
}


/**
 * NavGroup — fila de menú con sub-opciones colapsables.
 * - En modo expandido: muestra padre clickeable (toggle expand) + sub-items indentados.
 * - En modo collapsed: muestra solo el icono del padre; al click navega a la 1a child.
 * - Auto-expandido cuando la URL actual está dentro del matchPath del padre.
 */
function NavGroup({ item, isCollapsed, location, navigate, onNavigate, renderIcon }) {
  const matchBase = item.matchPath || item.path;
  const groupActive = location.pathname.startsWith(matchBase);
  // Cuál es el item hijo actualmente activo (la fila más larga prefijo de la URL).
  const childActivePath = (() => {
    const matches = item.children
      .filter(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))
      .sort((a, b) => b.path.length - a.path.length);
    return matches[0]?.path;
  })();

  const [open, setOpen] = useState(groupActive);
  // Si el usuario navega a una sub-ruta desde afuera, abrir el grupo.
  useEffect(() => { if (groupActive) setOpen(true); }, [groupActive]);

  if (isCollapsed) {
    // Modo colapsado: solo icono del padre — navega a la 1ª child no admin-only-bloqueada
    const firstChild = item.children[0];
    return (
      <Tooltip title={item.text} placement="right" arrow>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => { navigate(firstChild.path); onNavigate?.(); }}
            sx={{
              borderRadius: 2,
              bgcolor: groupActive ? 'rgba(30, 58, 95, 0.08)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
              justifyContent: 'center', px: 1,
            }}
          >
            <ListItemIcon sx={{
              color: groupActive ? '#164e63' : '#94a3b8',
              minWidth: 0, justifyContent: 'center',
            }}>
              {renderIcon(item)}
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => setOpen(o => !o)}
          sx={{
            borderRadius: 2,
            bgcolor: groupActive ? 'rgba(30, 58, 95, 0.08)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
            px: 2,
          }}
        >
          <ListItemIcon sx={{
            color: groupActive ? '#164e63' : '#94a3b8',
            minWidth: 40, justifyContent: 'center',
          }}>
            {renderIcon(item)}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: groupActive ? 600 : 400,
                color: groupActive ? '#164e63' : '#64748b',
                fontSize: 14,
              }
            }}
          />
          {open ? <ExpandLessIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                : <ExpandMoreIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout={150} unmountOnExit>
        <List disablePadding sx={{ pl: 1.5 }}>
          {item.children.map(c => {
            const sel = c.path === childActivePath;
            return (
              <ListItem key={c.text} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => { navigate(c.path); onNavigate?.(); }}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: sel ? 'rgba(30, 58, 95, 0.10)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.05)' },
                    pl: 2.5, pr: 2, py: 0.5,
                  }}
                >
                  <ListItemIcon sx={{
                    color: sel ? '#164e63' : '#94a3b8',
                    minWidth: 30, justifyContent: 'center',
                  }}>
                    {React.cloneElement(c.icon, { sx: { fontSize: 16 } })}
                  </ListItemIcon>
                  <ListItemText
                    primary={c.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: sel ? 600 : 400,
                        color: sel ? '#164e63' : '#64748b',
                        fontSize: 13,
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}
